import type { Message } from '@/types/message-types';

export class MessageDataService {
  /**
   * Clean corrupted field data that comes from the database
   */
  static cleanCorruptedField(fieldValue: unknown, fieldName: string, instanceId: string): string | null {
    // If it's already a valid string, keep it
    if (typeof fieldValue === 'string' && fieldValue.length > 0) {
      return fieldValue;
    }
    
    // Handle corrupted objects
    if (fieldValue && typeof fieldValue === 'object') {
      const obj = fieldValue as Record<string, unknown>;
      if (obj._type === 'undefined' || obj.value === 'undefined') {
        console.warn(`[${instanceId}] ⚠️ Corrupted ${fieldName} object detected, setting to null:`, obj);
        return null;
      } else if (obj.value && typeof obj.value === 'string') {
        console.warn(`[${instanceId}] 🔧 Extracted ${fieldName} from object:`, obj.value);
        return obj.value;
      } else {
        console.warn(`[${instanceId}] ⚠️ Unknown ${fieldName} object format, setting to null:`, obj);
        return null;
      }
    }
    
    // Handle invalid types
    if (fieldValue !== null && fieldValue !== undefined && typeof fieldValue !== 'string') {
      console.warn(`[${instanceId}] ⚠️ Invalid ${fieldName} type (${typeof fieldValue}), setting to null:`, fieldValue);
      return null;
    }
    
    return fieldValue;
  }

  /**
   * Clean and validate a single message from database
   */
  static cleanSingleMessage(rawMessage: Record<string, unknown>, instanceId: string): Message {
    const cleanWhatsappId = this.cleanCorruptedField(rawMessage.external_id, 'external_id', instanceId);
    const cleanDeliveredAt = this.cleanCorruptedField(rawMessage.delivered_at, 'delivered_at', instanceId);
    const cleanReadAt = this.cleanCorruptedField(rawMessage.read_at, 'read_at', instanceId);

    // Determine status based on sender type, timestamps and external_id
    let status: Message['status'] = 'sent';
    
    // Check if this is an outgoing message (human_agent/ai messages)
    const isOutgoing = rawMessage.sender_type === 'human_agent' || 
                      rawMessage.sender_type === 'ai_agent_automatic' ||
                      rawMessage.sender_type === 'ai_with_human_review_no_changes' ||
                      rawMessage.sender_type === 'ai_with_human_review_with_changes';
    
    if (isOutgoing) {
      // For outgoing messages, check WhatsApp delivery status
      if (!cleanWhatsappId) {
        // No WhatsApp ID means the send failed or is still pending
        status = rawMessage.status === 'failed' ? 'failed' : 'sending';
      } else if (cleanReadAt) {
        status = 'read';
      } else if (cleanDeliveredAt) {
        status = 'delivered';
      } else {
        status = 'sent';
      }
    } else {
      // For incoming messages (customer), always show as delivered
      status = 'delivered';
    }

    return {
      id: rawMessage.id,
      conversation_id: rawMessage.conversation_id,
      sender_type: rawMessage.sender_type,
      sender_id: rawMessage.sender_id,
      content: rawMessage.content,
      message_type: rawMessage.message_type || 'text',
      external_id: cleanWhatsappId,
      media_url: rawMessage.media_url || null,
      metadata: rawMessage.metadata,
      is_read: rawMessage.is_read || false,
      created_at: rawMessage.created_at,
      delivered_at: cleanDeliveredAt,
      read_at: cleanReadAt,
      status,
      ai_model: rawMessage.ai_model || null,
      ai_suggestion_data: rawMessage.ai_suggestion_data || null,
      change_detection_metadata: rawMessage.change_detection_metadata || null
    };
  }

  /**
   * Clean and validate an array of messages from database
   */
  static cleanMessageArray(rawMessages: unknown[], instanceId: string): Message[] {
    if (!Array.isArray(rawMessages)) {
      console.warn(`[${instanceId}] ⚠️ Invalid message array, returning empty array`);
      return [];
    }

    return rawMessages.map(msg => this.cleanSingleMessage(msg, instanceId));
  }

  /**
   * Create an optimistic message for immediate UI feedback
   */
  static createOptimisticMessage(
    conversationId: string,
    senderId: string,
    content: string,
    messageType: string = 'text',
    aiModel?: string,
    senderType: string = 'human_agent',
    aiSuggestionData?: Record<string, unknown>
  ): Message {
    return {
      id: `optimistic-${Date.now()}-${Math.random()}`,
      conversation_id: conversationId,
      sender_type: senderType as string,
      sender_id: senderId,
      content,
      message_type: messageType as Message['message_type'],
      external_id: null,
      metadata: aiSuggestionData ? {
        isOptimistic: true,
        ...aiSuggestionData
      } : null,
      is_read: false,
      created_at: new Date().toISOString(),
      delivered_at: null,
      read_at: null,
      status: 'sending',
      ai_model: aiModel || null,
      ai_suggestion_data: aiSuggestionData || null,
      change_detection_metadata: aiSuggestionData?.changeMetadata || null
    };
  }

  /**
   * Sort messages by creation date with proper error handling
   */
  static sortMessagesByDate(messages: Message[]): Message[] {
    if (!Array.isArray(messages)) {
      console.warn('Invalid messages array provided to sortMessagesByDate');
      return [];
    }

    const sortedMessages = [...messages].sort((a, b) => {
      try {
        // Extract date string from complex objects if needed
        const getDateString = (dateValue: unknown): string => {
          if (typeof dateValue === 'string') return dateValue;
          if (dateValue && typeof dateValue === 'object' && dateValue.value) return dateValue.value;
          if (dateValue && typeof dateValue === 'object' && dateValue.toString) return dateValue.toString();
          return '';
        };
        
        const dateStringA = getDateString(a.created_at);
        const dateStringB = getDateString(b.created_at);
        const dateA = new Date(dateStringA);
        const dateB = new Date(dateStringB);
        
        // Handle invalid dates
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          console.warn('Invalid date found in message sorting:', {
            messageA: { id: a.id, created_at: a.created_at, type: typeof a.created_at },
            messageB: { id: b.id, created_at: b.created_at, type: typeof b.created_at }
          });
          return 0;
        }
        
        return dateA.getTime() - dateB.getTime(); // Oldest first in array
      } catch (error) {
        console.error('Error sorting messages:', error);
        return 0;
      }
    });

    return sortedMessages;
  }

  /**
   * Get message status display info
   */
  static getMessageStatusInfo(message: Message): {
    icon: string;
    color: string;
    tooltip: string;
  } {
    const isOutgoing = message.sender_type === 'human_agent' || 
                      message.sender_type === 'ai_agent_automatic' ||
                      message.sender_type === 'ai_with_human_review_no_changes' ||
                      message.sender_type === 'ai_with_human_review_with_changes';
    
    switch (message.status) {
      case 'sending':
        return {
          icon: 'clock',
          color: 'text-muted-foreground',
          tooltip: 'Sending message...'
        };
      case 'sent':
        return {
          icon: 'check',
          color: 'text-muted-foreground',
          tooltip: isOutgoing ? 'Message sent' : 'Message received'
        };
      case 'delivered':
        return {
          icon: 'check-check',
          color: 'text-muted-foreground',
          tooltip: isOutgoing ? 'Message delivered' : 'Message received'
        };
      case 'read':
        return {
          icon: 'check-check',
          color: 'text-primary',
          tooltip: 'Message read'
        };
      case 'failed':
        return {
          icon: 'x-circle',
          color: 'text-destructive',
          tooltip: 'Failed to send message'
        };
      default:
        return {
          icon: 'clock',
          color: 'text-muted-foreground',
          tooltip: 'Unknown status'
        };
    }
  }
}
